import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { ActionData } from '@bonusly/common-action-input';
import { createBonusData } from '@bonusly/common-give-box';
import { useDispatch } from '@bonusly/events';
import { useHttpClient, type HttpResponse } from '@bonusly/http-client';
import { useCurrentUser, useIdentity } from '@bonusly/who-am-i';

import { Bonus, CachedBonus, CachedBonuses } from '../../../../../../types/bonus';
import { addBonusToParent, addBonusToParentInProfile, addCommentToSingleBonus, CachedProfileBonuses } from '../../../cache/mutators/bonus';
import { subtractValueFromBalance } from '../../../cache/mutators/user';
import { getRecognitionType } from '../../../components/bonus/utils';
import { CommentEvent } from '../../../events';
import { QueryKey } from '../../../hooks/types';
import { useBonusAnalytics } from '../../../hooks/use-bonus-analytics';
import { useContextualizedBonusDetails } from '../../../hooks/use-contextualized-bonus-details';
import { ProfileResponse } from '../../../types/profile';
import { getCelebrationType, isCelebration } from '../../../utils';

interface UseCreateCommentConfig {
	onError?: (error: unknown) => void;
	onSuccess?: VoidFunction;
	via?: string;
	selectedSuggestionRef?: React.RefObject<string | null>;
}

export const useCreateComment = (bonus: Bonus, { onError, onSuccess, via, selectedSuggestionRef }: UseCreateCommentConfig = {}) => {
	const analytics = useBonusAnalytics();
	const { isUserReceiver } = useContextualizedBonusDetails(bonus);
	const queryClient = useQueryClient();
	const http = useHttpClient('v2');
	const { refetchProfile } = useIdentity();
	const dispatch = useDispatch();
	const currentUser = useCurrentUser();

	const { mutate, isLoading: isSubmitting } = useMutation<HttpResponse<{ result: Bonus }>, unknown, ActionData, unknown>(
		(action) => {
			const data = {
				bonus: createBonusData({ action, bonus }),
			};

			const params = {
				include_children: true,
			};

			if (via) {
				Object.assign(params, { application_name: via });
			}

			return http.post('/bonuses', data, { params });
		},
		{
			onError: (err: unknown) => {
				onError?.(err);
			},
			onSuccess: ({ data: { result: comment } }, vars) => {
				if (isCelebration(bonus)) queryClient.invalidateQueries([QueryKey.CelebrationsWidget]);

				queryClient.setQueriesData<CachedBonuses>([QueryKey.BonusesInfinite], (cached) => addBonusToParent(comment, cached));

				queryClient.setQueryData<CachedBonus>([QueryKey.Bonus, bonus.id], (cached) => {
					if (cached != null) {
						return addCommentToSingleBonus(comment, cached);
					}
				});

				queryClient.setQueriesData<CachedProfileBonuses>([QueryKey.ProfileBonuses], (cached) => addBonusToParentInProfile(comment, cached));

				queryClient.setQueryData<ProfileResponse>([QueryKey.UserProfile], (cached) => subtractValueFromBalance(vars.totalAmount, cached));

				const suggestion = selectedSuggestionRef?.current;
				const usedSuggestion = suggestion != null;
				const suggestionModified = usedSuggestion && vars.text.trim() !== suggestion.trim();

				analytics.trackCommentSubmitSuccess({
					amount: vars.totalAmount,
					gifIncluded: vars.attachment?.category === 'social',
					imageIncluded: vars.attachment?.category === 'image',
					spotlightIncluded: vars.attachment?.category === 'employee_spotlight',
					hashtagsCount: vars.hashtags.length,
					recipientsCount: vars.recipients.length,
					userTagsCount: vars.mentions.length,
					isThankYou: isUserReceiver,
					wordCount: vars.text.split(' ').length,
					recognitionType: getRecognitionType(bonus),
					usedSuggestion,
					suggestionModified,
				});

				// NOTE: refetch profile to update give box pill balances
				refetchProfile();

				dispatch(CommentEvent.Created, { totalAmount: vars.totalAmount });

				// Dispatch celebration-specific events for Intercom integration
				if (isCelebration(bonus)) {
					const celebrationData = {
						celebrationId: bonus.id,
						type: getCelebrationType(bonus),
						amount: vars.totalAmount,
						userId: currentUser.id,
					};
					dispatch('Celebration Comment Created', celebrationData);
				}

				onSuccess?.();
			},
		}
	);

	return {
		isSubmitting,
		onSubmit: mutate,
	};
};
